import { Video, Microscope, PenTool, Lightbulb, BookOpen, Check, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import BookmarkIcon from './BookmarkIcon';

const iconMap = {
  Video: Video, // Video icon type maps to Video icon
  Microscope: Microscope,
  PenTool: PenTool,
  Lightbulb: Lightbulb,
  GraduationCap: GraduationCap // For Basic Concepts
};

const typeGradients = {
  Watch: 'from-blue-500 via-purple-500 to-pink-500',
  Video: 'from-red-500 via-pink-500 to-purple-500', // Video type has red-pink-purple gradient
  Explore: 'from-green-400 via-teal-500 to-cyan-500',
  Practice: 'from-orange-400 via-red-500 to-pink-600',
  Curiosity: 'from-yellow-400 via-amber-500 to-orange-500',
  BasicConcepts: 'from-indigo-500 via-purple-500 to-pink-500' // Basic Concepts gradient
};

// Get icon based on annotation type (not icon type)
const getIconForType = (type, iconType) => {
  // Watch type should show BookOpen icon (Learn & Discover)
  if (type === 'Watch') {
    return BookOpen;
  }
  // Video type should show Video icon
  if (type === 'Video') {
    return Video;
  }
  // BasicConcepts type should show GraduationCap icon
  if (type === 'BasicConcepts') {
    return GraduationCap;
  }
  // For other types, use the iconMap based on iconType
  return iconMap[iconType] || BookOpen;
};

export default function FloatingActivityIcon({
  item,
  isCompleted,
  onClick,
  position,
  activeAnnotationId = null,
  annotationId = null,
  chapterId = null, // Added to support bookmarking
  isBookmarked: propIsBookmarked = false,
  bookmarkId = null,
  onBookmarkToggle = null
}) {
  const Icon = getIconForType(item.type, item.icon);
  // Always use the original gradient, even when completed
  const gradient = typeGradients[item.type] || typeGradients.Watch;

  // Blink state for visual focus when navigated to
  const shouldBlink = (activeAnnotationId && annotationId && String(activeAnnotationId) === String(annotationId));

  // Track bookmark state locally to control visibility
  const [isBookmarked, setIsBookmarked] = useState(propIsBookmarked);

  // Sync with prop when it changes (e.g. initial load or parent update)
  useEffect(() => {
    setIsBookmarked(propIsBookmarked);
  }, [propIsBookmarked]);

  return (
    <div
      className="absolute z-20 pointer-events-auto group" // Added group here for hover targeting
      style={position}
    >
      {/* Bookmark Icon - Shows on hover OR if bookmarked. Moved to LEFT to avoid checkmark overlap */}
      {chapterId && (
        <div
          className={`absolute -top-2 -left-2 z-30 transition-all duration-200 scale-75 hover:scale-100 ${isBookmarked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <BookmarkIcon
            chapterId={chapterId}
            sectionName={item.section_name || 'Learning Pack'}
            sectionNumber={item.section_number}
            bookmarkType="activity"
            contentId={item.id || item.annotation_id}
            contentData={{
              title: item.title,
              preview_text: item.description,
              content_type: item.type
            }}
            initialBookmarked={isBookmarked}
            bookmarkId={bookmarkId}
            onToggle={(active, bookmark) => {
              setIsBookmarked(active);
              if (onBookmarkToggle) {
                onBookmarkToggle(item.id || item.annotation_id, active, bookmark?.id);
              }
            }}
            className={`bg-white/90 shadow-sm rounded-full p-1 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 hover:text-[hsl(var(--primary))] ${isBookmarked ? 'text-[hsl(var(--primary))] fill-[hsl(var(--primary))]' : 'text-muted-foreground'}`}
          />
        </div>
      )}

      <Button
        onClick={onClick}
        className={`
          relative 
            w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full p-0
          bg-gradient-to-br ${gradient}
          border-2 ${isCompleted ? 'border-green-400/70 ring-2 ring-green-400/40' : 'border-white/30'}
          shadow-2xl
          opacity-50 hover:opacity-100
          ${isCompleted ? '' : 'animate-pulse-glow hover:scale-110'}
          ${shouldBlink ? 'annotation-blink' : ''}
          transition-all duration-300
          cursor-pointer
        `}
      >
        {/* Icon - Keep original color and full opacity when completed */}
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white" />

        {/* Checkmark for completed - Enhanced with better visibility */}
        {isCompleted && (
          <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg z-10 animate-in fade-in zoom-in duration-200">
            <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-white font-bold" strokeWidth={3} />
          </div>
        )}

        {/* Glow effect - Show for both completed and uncompleted, but different intensity */}
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${gradient} opacity-50 blur-xl ${isCompleted ? 'opacity-40' : 'group-hover:opacity-75'} transition-opacity`} />
      </Button>

      {/* Duration label with dark background */}
      <div className={`
        mt-2 mx-auto w-fit px-2 py-0.5 sm:py-1 rounded-full
        text-center text-[10px] sm:text-xs font-bold
        ${isCompleted
          ? 'text-green-300 bg-green-900/40 border border-green-500/30'
          : 'text-white bg-black/70'
        }
        backdrop-blur-sm
      `}>
        {item.duration}
      </div>
    </div>
  );
}
